import React, { useState, useEffect } from 'react';
import { Helmet } from "react-helmet";
import { useParams, Link } from 'react-router-dom';
import { FaCalendarAlt, FaUser, FaShareAlt, FaFacebook, FaTwitter, FaWhatsapp, FaLinkedin } from 'react-icons/fa';
import Navbar from './components/Navbar';
import Footer from './components/Footer';

const Blog = () => {
  const [postInfo, setPostInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showShareOptions, setShowShareOptions] = useState(false);
  const { slug } = useParams();

  useEffect(() => {
    setLoading(true);
    fetch(`${process.env.REACT_APP_API_URL}/post/${slug}`)
      .then(response => {
        response.json().then(postInfo => {
          setPostInfo(postInfo);
          setLoading(false);
        });
      })
      .catch(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-gray-300 border-t-custom-gray-head-text"></div>
        </div>
        <Footer />
      </>
    );
  }

  if (!postInfo) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
          <p className="text-2xl text-gray-600">Post not found</p>
        </div>
        <Footer />
      </>
    );
  }

  const postUrl = encodeURIComponent(window.location.href);
  const postTitle = encodeURIComponent(postInfo.name);

  return (
    <>
      <Helmet>
        <title>{postInfo.name} | Glintz Photography Blog</title>
        <meta name="description" content={postInfo.excerpt || postInfo.name} />
      </Helmet>

      <Navbar />

      {/* Hero Section with Header Image */}
      <div className="relative pt-20 lg:pt-24">
        <div 
          className="relative h-64 md:h-80 lg:h-96 bg-cover bg-center"
          style={{
            backgroundImage: "url(https://glintzphotography.org/images/academy-header.jpg)",
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70"></div>
          
          {/* Breadcrumb */}
          <div className="absolute top-8 left-0 right-0 px-6 md:px-12 lg:px-20">
            <div className="flex items-center text-white/90 text-sm md:text-base space-x-2">
              <Link to="/" className="hover:text-white transition-colors duration-200">
                Home
              </Link>
              <span>/</span>
              <Link to="/blog" className="hover:text-white transition-colors duration-200">
                Blog
              </Link>
              <span>/</span>
              <span className="text-white capitalize font-medium">{postInfo.name}</span>
            </div>
          </div>

          {/* Title Overlay */}
          <div className="absolute bottom-0 left-0 right-0 px-6 md:px-12 lg:px-20 pb-8">
            <div className="max-w-4xl">
              {/* <span className="inline-block px-4 py-1 bg-custom-gray-head-text/90 text-white text-xs md:text-sm uppercase tracking-wider rounded-full mb-4">
                {postInfo.category || 'Photography'}
              </span> */}
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white uppercase tracking-wide leading-tight">
                {postInfo.name}
              </h1>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <article className="bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-8 py-12 lg:py-16">
          
          {/* Meta Information */}
          <div className="flex flex-wrap items-center gap-4 md:gap-6 pb-8 mb-8 border-b-2 border-gray-200">
            <div className="flex items-center gap-2 text-custom-gray-text">
              <FaUser className="text-custom-gray-head-text" />
              <span className="text-sm md:text-base capitalize">{postInfo.author || 'Glintz Photography'}</span>
            </div>
            <div className="flex items-center gap-2 text-custom-gray-text">
              <FaCalendarAlt className="text-custom-gray-head-text" />
              <span className="text-sm md:text-base">{postInfo.date || new Date().toLocaleDateString()}</span>
            </div>
            {/* <div className="flex items-center gap-2 text-custom-gray-text">
              <FaClock className="text-custom-gray-head-text" />
              <span className="text-sm md:text-base">{postInfo.readTime || '5 min read'}</span>
            </div> */}

            {/* Share Button */}
            <div className="ml-auto relative">
              <button
                onClick={() => setShowShareOptions(!showShareOptions)}
                className="flex items-center gap-2 px-4 py-2 bg-custom-gray-head-text text-white rounded-full hover:bg-opacity-90 transition-all duration-300 shadow-md hover:shadow-lg"
              >
                <FaShareAlt className="text-blue-400" />
                <span className="text-sm font-medium text-blue-400">Share</span>
              </button>

              {showShareOptions && (
                <div className="absolute right-0 top-12 bg-white rounded-lg shadow-2xl p-4 z-10 min-w-[200px] border border-gray-200">
                  <div className="flex flex-col gap-2">
                    <a
                      href={`https://www.facebook.com/sharer/sharer.php?u=${postUrl}`}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center gap-3 px-4 py-2 rounded-md hover:bg-blue-50 transition-colors duration-200 text-gray-700"
                    >
                      <FaFacebook className="text-blue-600 text-lg" />
                      <span className="text-sm font-medium">Facebook</span>
                    </a>
                    <a
                      href={`https://twitter.com/intent/tweet?url=${postUrl}&text=${postTitle}`}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center gap-3 px-4 py-2 rounded-md hover:bg-sky-50 transition-colors duration-200 text-gray-700"
                    >
                      <FaTwitter className="text-sky-500 text-lg" />
                      <span className="text-sm font-medium">Twitter</span>
                    </a>
                    <a
                      href={`https://api.whatsapp.com/send?text=${postTitle} ${postUrl}`}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center gap-3 px-4 py-2 rounded-md hover:bg-green-50 transition-colors duration-200 text-gray-700"
                    >
                      <FaWhatsapp className="text-green-600 text-lg" />
                      <span className="text-sm font-medium">WhatsApp</span>
                    </a>
                    <a
                      href={`https://www.linkedin.com/sharing/share-offsite/?url=${postUrl}`}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center gap-3 px-4 py-2 rounded-md hover:bg-blue-50 transition-colors duration-200 text-gray-700"
                    >
                      <FaLinkedin className="text-blue-700 text-lg" />
                      <span className="text-sm font-medium">LinkedIn</span>
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Featured Image */}
          {postInfo.coverImage && (
            <div className="mb-12 rounded-2xl overflow-hidden shadow-2xl">
              <img
                src={postInfo.coverImage}
                alt={postInfo.name}
                className="w-full h-auto object-cover transform hover:scale-105 transition-transform duration-500"
                draggable="false"
                onContextMenu={(e) => e.preventDefault()}
              />
            </div>
          )}

          {/* Excerpt/Introduction */}
          {postInfo.excerpt && (
            <div className="mb-10 p-6 bg-gray-50 border-l-4 border-custom-gray-head-text rounded-r-lg">
              <p className="text-lg md:text-xl text-gray-700 italic leading-relaxed">
                {postInfo.excerpt}
              </p>
            </div>
          )}

          {/* Content Blocks */}
          <div className="prose prose-lg max-w-none">
            <div className="text-gray-800 leading-relaxed text-base md:text-lg">
              {postInfo.content?.map((block, index) => {
                if (block.text || block.content) {
                  return (
                    <p
                      key={index}
                      className="mb-6 md:mb-8 text-justify first-letter:text-5xl first-letter:font-bold first-letter:text-custom-gray-head-text first-letter:mr-2 first-letter:float-left first-letter:leading-none"
                    >
                      {block.text || block.content}
                    </p>
                  );
                }

                if (block.src) {
                  return (
                    <figure
                      key={index}
                      className="my-10 md:my-14"
                    >
                      <div className="rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-shadow duration-300">
                        <img
                          src={block.src}
                          alt={block.caption || ''}
                          draggable="false"
                          onContextMenu={(e) => e.preventDefault()}
                          className="w-full block transition-transform duration-500 ease-in-out pointer-events-none select-none hover:scale-105"
                          style={{ WebkitUserDrag: "none" }}
                        />
                      </div>
                      {block.caption && (
                        <figcaption className="text-center text-sm md:text-base italic mt-4 text-gray-600 px-4">
                          {block.caption}
                        </figcaption>
                      )}
                    </figure>
                  );
                }

                return null;
              })}
            </div>
          </div>

          {/* Tags Section */}
          {postInfo.tags && postInfo.tags.length > 0 && (
            <div className="mt-12 pt-8 border-t border-gray-200">
              <div className="flex flex-wrap gap-2">
                <span className="text-custom-gray-text font-medium mr-2">Tags:</span>
                {postInfo.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-gray-100 text-custom-gray-head-text text-sm rounded-full hover:bg-custom-gray-head-text hover:text-white transition-colors duration-200 cursor-pointer"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </article>

      {/* Follow Us Section */}
      {/*follow on socials section*/}
				<div className="grid grid-cols-1 text-center py-20 px-4 hover:bg-custom-dark-gray-bg transition">
					<h1 
						className="
							xl:text-4xl lg:text-4xl md:text-3xl text-2xl tracking-[.25em] uppercase mb-5 
							text-white">
							follow us on instagram
					</h1>
					<p className="mb-2 text-white italic text-xl">
						<a href="https://www.instagram.com/glintzweddingphotography?igsh=MTJibTkyZzhjczljeg==">@glintzweddingphotography</a></p>
					<p className="text-white italic text-xl">
						<a href="https://www.instagram.com/glintz_studios/">@glintz_studios</a></p>
				</div>

      <Footer />
    </>
  );
};

export default Blog;